import { FC, useState } from 'react'

import { Box, Spinner, Tag, useToggle } from '@factorialco/deprecated-design-system'
import { F0Icon, F0TagRaw, IconType } from '@factorialco/f0-react'
import { DropdownDefault, Suitcase as SuitcaseIcon } from '@factorialco/f0-react/icons/app'
import moment from 'moment'

import Popover from 'modules/core/components/Popover'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { ProjectClockInData } from 'modules/attendance/components/ClockIn/Tracker/tracker'
import { useGetEmployeeProjectsTrackingByMonth } from 'modules/project_management/hooks/useGetEmployeeProjectsTrackingByMonth'

import { IItem, NavigationSelector } from './NavigationSelector'

function Selector({
  text,
  isPlaceholder = false,
  icon,
}: {
  text?: string
  isPlaceholder?: boolean
  icon?: IconType
}) {
  return (
    <Box
      flexDirection="row"
      alignItems="center"
      gap="s4"
      alignSelf="start"
      className="cursor-default rounded-xs px-1 py-0.5 hover:bg-f1-background-hover"
    >
      {icon && (
        <Box className="translate-y-[2px]">
          <F0Icon icon={icon} className="text-f1-icon" />
        </Box>
      )}
      <span
        className={`font-medium
          ${!isPlaceholder ? 'text-f1-foreground' : 'text-f1-foreground-secondary'}
        `}
      >
        {text}
      </span>
      <F0Icon icon={DropdownDefault} />
    </Box>
  )
}

interface Props {
  disabled: boolean
  clockedIn: boolean
  setProjectData: (data: ProjectClockInData) => void
  projectData: ProjectClockInData
  factorialOneVersion?: boolean
}

export const ProjectSelector: FC<Props> = ({
  disabled,
  clockedIn,
  projectData,
  setProjectData,
  factorialOneVersion = false,
}) => {
  const { employee } = useCurrentSession()
  const employeeId = employee?.id
  if (!employeeId) throw new Error('Employee not found')
  const [isOpen, toggleOpen] = useToggle(false)
  const [selectedProject, setSelectedProject] = useState<IItem | undefined>()
  const startDate = moment().startOf('month').toDate()
  const endDate = moment().endOf('month').toDate()
  const includeSubprojects = true

  const {
    queryData: { projectsAssigned, hasProjectsToTrack, fetchingProjectWorkers },
  } = useGetEmployeeProjectsTrackingByMonth({
    employeeId,
    startDate,
    endDate,
    includeSubprojects,
  })

  const findSubproject = (id: string) => {
    return projectsAssigned
      .flatMap((projectWorker) => projectWorker.imputableProject?.subprojects)
      .find((value) => value?.id === id)
  }

  useUnsafeEffect(() => {
    if (projectData.subprojectId) {
      const subproject = findSubproject(projectData.subprojectId)
      if (subproject) {
        setSelectedProject({
          id: String(subproject.id),
          name: subproject.name,
          level: 2,
          parentId: projectData.projectWorkerId,
        })
      }
    } else {
      const project = projectsAssigned.find(
        (projectWorker) => projectWorker.id === projectData.projectWorkerId
      )

      if (project) {
        setSelectedProject({
          id: String(projectData.projectWorkerId),
          name: String(project.imputableProject?.name),
          level: 1,
        })
      }
    }
  }, [projectData, fetchingProjectWorkers]) // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = useUnsafeMemo(() => {
    if (projectData.subprojectId) {
      return selectedProject?.id !== projectData.subprojectId
    } else {
      return selectedProject?.id !== projectData.projectWorkerId
    }
  }, [selectedProject, projectData])

  const resolveTextLabel = useUnsafeMemo(() => {
    if (projectData.projectWorkerId) {
      const project = projectsAssigned.find(
        (projectWorker) => projectWorker.id === projectData.projectWorkerId
      )

      if (projectData.subprojectId) {
        const subproject = findSubproject(projectData.subprojectId)

        return `${subproject?.name} - ${project?.imputableProject?.name}`
      }

      return project?.imputableProject?.name
    }

    return clockedIn && !selectedProject
      ? i18n.t('clock_in.widget.no_project')
      : i18n.t('clock_in.widget.select_project')
  }, [selectedProject, clockedIn, projectsAssigned, projectData]) // eslint-disable-line react-hooks/exhaustive-deps

  if (fetchingProjectWorkers && !factorialOneVersion) {
    return (
      <Box alignItems="center" justifyContent="center" height="full">
        <Spinner />
      </Box>
    )
  }

  if (!hasProjectsToTrack) return null

  const buildProjectData = (): IItem[] => {
    if (!projectsAssigned) return []

    return projectsAssigned.flatMap((projectWorker) => {
      const { id, imputableProject } = projectWorker

      return {
        id: String(id),
        name: String(imputableProject?.name),
        level: 1,
        children: imputableProject?.subprojects?.map((subproject) => {
          return {
            level: 2,
            parentId: String(id),
            id: String(subproject.id),
            name: String(subproject.name),
          }
        }),
      }
    })
  }

  const confirmSelection = (item?: IItem) => {
    if (item?.id) {
      setProjectData({
        projectWorkerId: item?.level === 1 ? item.id : item?.parentId,
        subprojectId: item?.level === 2 ? item.id : undefined,
      })
    } else {
      setSelectedProject(undefined)
      setProjectData({
        projectWorkerId: undefined,
        subprojectId: undefined,
      })
    }
    toggleOpen(false)()
  }

  const toggle = (newValue: boolean) => () => !disabled && toggleOpen(newValue)()

  return (
    <Popover attachment="top center" size="bigger" open={isOpen} toggle={toggle}>
      {factorialOneVersion ? (
        <Box>
          {disabled ? (
            <Box
              style={{
                cursor: 'auto',
              }}
            >
              <F0TagRaw text={resolveTextLabel ?? '-'} icon={SuitcaseIcon} />
            </Box>
          ) : (
            <Selector
              text={resolveTextLabel ?? ''}
              isPlaceholder={resolveTextLabel === i18n.t('clock_in.widget.select_project')}
              icon={SuitcaseIcon}
            />
          )}
        </Box>
      ) : (
        <Box
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          width="fit-content"
        >
          <Tag
            disabled={disabled}
            icon="briefcase"
            leftItem="icon"
            type="grey"
            rightItem="dropdown"
            showActionIcon
          >
            {resolveTextLabel}
          </Tag>
        </Box>
      )}
      <NavigationSelector
        isDirty={isDirty}
        titleIcon="briefcase"
        items={buildProjectData()}
        selectedItem={selectedProject}
        setSelectedItem={setSelectedProject}
        confirmSelection={confirmSelection}
        title={i18n.t('clock_in.widget.select_project')}
        noSelectionTitle={i18n.t('clock_in.widget.no_project')}
      />
    </Popover>
  )
}
